import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Tooltip,
} from "@material-ui/core";
import PostAddIcon from "@material-ui/icons/PostAdd";
import { Autocomplete } from "@material-ui/lab";
import React, { useMemo, useState } from "react";
import { useMutation, useNotify, useQuery, useRefresh, useTranslate } from "react-admin";

import { OrderGroupCargoType } from "../../../entities/OrderGroupCargoType";
import { OrderStatusAddToGroupAllowed } from "../../../entities/OrderStatus";
import { useNotifyError } from "../../../utils/notifiers/useNotifyError";

const DATE_FIELD_BY_CARGO_TYPE = {
	[OrderGroupCargoType.outgoing]: "etd_date",
	[OrderGroupCargoType.incoming]: "eta_date",
};

export const AddOrderToGroupButton = ({ record }) => {
	const translate = useTranslate();
	const notify = useNotify();
	const notifyError = useNotifyError();
	const refresh = useRefresh();

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState(null);

	const memberIds = useMemo(
		() => (record?.orders || []).map((order) => order.order_id),
		[record?.orders]
	);
	const dateField = DATE_FIELD_BY_CARGO_TYPE[record?.cargo_type];
	const groupDate = (record?.orders || []).map((order) => order[dateField]).find(Boolean);

	const { data: orders, loading } = useQuery(
		{
			type: "getList",
			resource: "orders",
			payload: {
				filter: {
					terminal_id: [record?.terminal_id],
					[dateField]: groupDate,
					status: OrderStatusAddToGroupAllowed,
					"id:nin": memberIds,
					not_in_taken_group_cargo_type: record?.cargo_type,
				},
				sort: { field: "id", order: "ASC" },
				pagination: { page: 1, perPage: 1000 },
			},
		},
		{
			enabled: isDialogOpen && Boolean(groupDate),
			onFailure: notifyError,
		}
	);

	const [addOrderToGroup, { loading: isSaving }] = useMutation(
		{
			type: "create",
			resource: `order-groups/${record?.id}/add-order`,
			payload: { data: { order_id: selectedOrder?.id } },
		},
		{
			onSuccess: () => {
				notify(translate("resources.order-groups.add_order.success"), "info");
				setIsDialogOpen(false);
				refresh();
			},
			onFailure: notifyError,
		}
	);

	const openDialog = (event) => {
		event.stopPropagation();
		setSelectedOrder(null);
		setIsDialogOpen(true);
	};

	const closeDialog = () => setIsDialogOpen(false);

	if (!record?.id || record.is_task_completed) return null;

	return (
		<>
			<Tooltip title={translate("resources.order-groups.add_order.title")}>
				<IconButton size="small" onClick={openDialog}>
					<PostAddIcon />
				</IconButton>
			</Tooltip>
			<Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
				<DialogTitle>{translate("resources.order-groups.add_order.title")}</DialogTitle>
				<DialogContent>
					<Autocomplete
						options={orders || []}
						value={selectedOrder}
						loading={loading}
						onChange={(_event, value) => setSelectedOrder(value)}
						getOptionLabel={(order) => `${order.id} — ${order.reference || ""}`}
						noOptionsText={translate(
							groupDate
								? "resources.order-groups.add_order.empty"
								: "resources.order-groups.add_order.no_group_date"
						)}
						renderInput={(params) => (
							<TextField
								{...params}
								label={translate("resources.order-groups.add_order.order")}
								variant="outlined"
								margin="dense"
							/>
						)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeDialog}>{translate("ra.action.cancel")}</Button>
					<Button
						color="primary"
						variant="contained"
						disabled={!selectedOrder || isSaving}
						onClick={addOrderToGroup}
					>
						{translate("resources.order-groups.add_order.submit")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
